import React from "react";
import { TimeEntryConflictItem } from "@myapp/contract";
import Modal from "../../overlay/Modal/Modal";
import { Button } from "../../controls/Button/Button";

type Props = {
  open: boolean;
  conflicts: TimeEntryConflictItem[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
};

function formatDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const fmtDate = (d: Date) =>
    d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const sameDay = fmtDate(start) === fmtDate(end);
  return sameDay
    ? `${fmtDate(start)} ${fmtTime(start)} – ${fmtTime(end)}`
    : `${fmtDate(start)} ${fmtTime(start)} – ${fmtDate(end)} ${fmtTime(end)}`;
}

export const ConflictDialog: React.FC<Props> = ({
  open,
  conflicts,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  return (
    <Modal
      empty
      show={open}
      setShow={(val) => { if (!val && !isLoading) onCancel(); }}
      className="flex max-h-[90vh] w-[520px] flex-col gap-4 overflow-hidden p-6"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke="#D97706"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-900">
          Terminüberschneidung erkannt
        </p>
      </div>

      <p className="text-sm text-gray-600">
        Diese Abwesenheit überschneidet sich mit folgenden Einträgen. Möchten Sie trotzdem
        fortfahren? Die betroffenen Termine werden zur Neuplanung markiert.
      </p>

      <div className="flex-1 overflow-y-auto rounded-md border border-gray-200 bg-gray-50">
        {conflicts.map((c, i) => (
          <div
            key={`${c.type}:${c.id}:${c.technicianId}`}
            className={`flex items-start justify-between gap-2 px-3 py-2 text-sm ${
              i > 0 ? "border-t border-gray-200" : ""
            }`}
          >
            <div className="min-w-0">
              <p className="font-medium text-gray-800 truncate">{c.label}</p>
              <p className="text-xs text-gray-500">{c.technicianName}</p>
            </div>
            <span className="flex-shrink-0 text-xs text-gray-500">
              {formatDateRange(c.startTime, c.endTime)}
            </span>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 rounded-[30px] bg-amber-600 font-bold text-white h-[46px]"
          type="button"
        >
          {isLoading ? "Speichern..." : "Trotzdem speichern"}
        </Button>
        <Button
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 rounded-[30px] bg-[#716D6B] font-bold text-white h-[46px]"
          type="button"
        >
          Abbrechen
        </Button>
      </div>
    </Modal>
  );
};

export default ConflictDialog;
